import {combine, forward, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {InnerFilters, InnerSortings} from 'types/grid2.0';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    clearDataCacheFx,
    data$,
    defaultFilters,
    defaultSortings,
    fetchDataFx,
    fetchDataFiltersFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    gridCacheChanged,
    gridCacheReset,
    gridTokensCache$,
    pageNumber$,
    pageSize$,
    refetchWithSortByDescDate,
    search$,
    searchChanged,
    searchReset,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateCacheChanged,
    tableStateChanged,
    updOnly$,
    $invoicesFilters,
} from './index';

data$.on(fetchDataFx.done, (state, {result}) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

gridTokensCache$
    .on(fetchDataFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

forward({
    from: tableStateCacheChanged,
    to: clearDataCacheFx,
});

sample({
    clock: refetchWithSortByDescDate,
    fn: () => [{id: 'createdAt', value: true}],
    target: sortingsChanged,
});

forward({
    from: refetchWithSortByDescDate,
    to: searchReset,
});

const gridFetchSource = combine({
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    search: search$,
    updOnly: updOnly$,
    cache: gridTokensCache$,
});

interface CombinedGridFetchSource {
    sortings: InnerSortings;
    pageNumber: number;
    pageSize: number;
    filters: InnerFilters;
    search: string;
    updOnly: boolean;
    cache: Record<number, string | null>;
}
const mapGridFetchParams = ({
    pageNumber,
    pageSize,
    filters,
    sortings,
    updOnly,
    search,
    cache,
}: CombinedGridFetchSource) => ({
    paging: {pageNumber, pageSize},
    filters: mapFilters(filters),
    sortings: mapSortings(sortings),
    search,
    updOnly,
    pageRef: cache?.[pageNumber] ?? null,
});

sample({
    clock: Gate.open,
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFiltersFx,
});

sample({
    clock: merge([tableStateChanged, searchChanged]),
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFx,
});

$invoicesFilters.on(fetchDataFiltersFx.doneData, (_, result) => result).reset(Gate.close);

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
